import { useEffect, useState } from "react";
import { ArrowRightIcon, CheckIcon, PencilIcon, RefreshCwIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { HistorialDeVersiones } from "@/components/generacion/HistorialDeVersiones";
import { TextoConCitas } from "@/components/generacion/TextoConCitas";
import { VerificacionDeLaSeccion } from "@/components/generacion/VerificacionDeLaSeccion";
import { useHechos } from "@/hooks/useGeneracion";
import { useAccionDeLaSeccion, useEditarTexto, useRegenerarSeccion } from "@/hooks/useRedaccion";
import { getApiErrorMessage } from "@/lib/api";
import { formatearFechaYHora } from "@/lib/fechas";
import type { PersonaDeLaSeccion, Redaccion, SeccionDelInforme } from "@/lib/redaccion";

interface RevisionDeLaSeccionProps {
  informeId: number;
  seccion: SeccionDelInforme;
  redaccion: Redaccion;
  /** La IA no puede redactar ahora (dudas sin responder o sin análisis): Regenerar se apaga. */
  cerrada?: boolean;
  /** La siguiente sección de IA por revisar, si queda alguna. */
  siguiente: SeccionDelInforme | null;
  onIr: (itemId: number) => void;
  /** Hay un borrador a mano sin guardar: el contenedor avisa antes de dejarlo. */
  onEdicionSucia: (sucia: boolean) => void;
}

function linea(accion: string, persona: PersonaDeLaSeccion | null, cuando: string | null): string {
  const por = persona?.name ? ` por ${persona.name}` : "";

  return `${accion}${por}${cuando ? ` · ${formatearFechaYHora(cuando)}` : ""}`;
}

/**
 * Una sección con texto: se lee con sus citas, se mira lo que verificó el
 * servidor y se decide — regenerar, editar a mano, validar o reabrir.
 *
 * Validar NO está bloqueado por los errores de verificación: el servidor lo
 * permite y la decisión es de la persona. Se muestran de frente y se avisa.
 */
export function RevisionDeLaSeccion({ informeId, seccion, redaccion: r, cerrada = false, siguiente, onIr, onEdicionSucia }: RevisionDeLaSeccionProps) {
  const hechos = useHechos(informeId).data ?? [];
  const regenerar = useRegenerarSeccion(informeId);
  const editar = useEditarTexto(informeId);
  const accion = useAccionDeLaSeccion(informeId);

  const [editando, setEditando] = useState(false);
  const [borrador, setBorrador] = useState("");
  const [instruccion, setInstruccion] = useState("");

  const texto = r.texto ?? "";
  const validada = r.estado === "validada";
  const sucia = editando && borrador !== texto;
  const errores = r.verificacion?.errores.length ?? 0;

  useEffect(() => {
    onEdicionSucia(sucia);

    return () => onEdicionSucia(false);
  }, [sucia, onEdicionSucia]);

  const falla = [regenerar.error, editar.error, accion.error].find((e) => e !== null);
  const ocupada = regenerar.isPending || editar.isPending || accion.isPending;

  function actuar(a: "validar" | "reabrir" | "mantener") {
    accion.mutate({ itemId: seccion.item_id, accion: a });
  }

  function regenerarLaSeccion(conInstruccion: string | null) {
    regenerar.mutate(
      { itemId: seccion.item_id, instruccion: conInstruccion },
      { onSuccess: () => setInstruccion("") },
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {r.desactualizada ? (
        <div className="bg-desactualizada-soft flex items-start gap-3 rounded-[0.875rem] px-4 py-3.5">
          <RefreshCwIcon aria-hidden className="text-desactualizada-foreground mt-0.5 size-4 shrink-0" />
          <div className="flex min-w-0 flex-1 flex-col gap-2.5">
            <div>
              <p className="text-desactualizada-fuerte text-[0.8125rem] font-bold">
                Esta sección quedó desactualizada: cambió algo de lo que depende
              </p>
              <p className="text-desactualizada-foreground text-xs font-semibold">
                El texto se mantiene hasta que decidas.
              </p>
            </div>
            {r.motivos.length > 0 ? (
              <ul className="flex flex-col gap-1">
                {r.motivos.map((motivo) => (
                  <li key={motivo} className="text-desactualizada-foreground text-xs font-semibold text-pretty">
                    {motivo}
                  </li>
                ))}
              </ul>
            ) : null}
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                className="h-auto rounded-[0.625rem] px-3 py-1.5"
                disabled={ocupada || cerrada}
                onClick={() => regenerarLaSeccion(null)}
              >
                Regenerar con los datos nuevos
              </Button>
              <button
                type="button"
                disabled={ocupada}
                onClick={() => actuar("mantener")}
                className="bg-card text-ink rounded-[0.625rem] px-3 py-1.5 text-xs font-bold disabled:opacity-50"
              >
                Mantener el texto
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {validada ? (
        <div className="flex flex-col gap-0.5">
          <p className="text-ink text-xs font-bold">{linea("Validada", r.validada_por, r.validada_en)}</p>
          {r.mantenida_en ? <p className="text-ink-muted text-xs font-semibold">{linea("Texto mantenido", r.mantenida_por, r.mantenida_en)}</p> : null}
        </div>
      ) : null}

      {editando ? (
        <div className="flex max-w-3xl flex-col gap-2">
          <Textarea
            aria-label="Texto de la sección"
            value={borrador}
            onChange={(e) => setBorrador(e.target.value)}
            className="min-h-56 leading-relaxed"
          />
          <p className="text-ink-muted text-xs font-semibold text-pretty">
            Las cifras van entre dobles corchetes: <code className="font-mono">[[valor|clave]]</code>, por ejemplo{" "}
            <code className="font-mono">[[120,00 ha|superficie_total]]</code>. La clave es la de la hoja de hechos y el
            servidor comprueba que el valor coincida.
          </p>
          <div className="flex flex-wrap items-center gap-2.5">
            <span className={sucia ? "text-warning-texto flex-1 text-xs font-bold" : "text-ink-medio flex-1 text-xs font-semibold"}>
              {sucia ? "Cambios sin guardar" : "Sin cambios"}
            </span>
            <Button type="button" size="sm" variant="outline" onClick={() => setEditando(false)}>
              Descartar
            </Button>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={!sucia || borrador.trim() === "" || editar.isPending}
              onClick={() =>
                editar.mutate({ itemId: seccion.item_id, texto: borrador }, { onSuccess: () => setEditando(false) })
              }
            >
              Guardar edición
            </Button>
          </div>
        </div>
      ) : (
        <TextoConCitas texto={texto} verificacion={r.verificacion} hechos={hechos} />
      )}

      {!editando ? (
        <VerificacionDeLaSeccion texto={texto} verificacion={r.verificacion} hechos={hechos} fuentes={r.fuentes} faltantes={r.faltantes} />
      ) : null}

      {falla ? (
        <p role="alert" className="text-danger-texto text-xs font-semibold text-pretty">
          {getApiErrorMessage(falla, "No pudimos completar la acción. Inténtalo de nuevo.")}
        </p>
      ) : null}

      {!editando ? (
        <div className="border-divisor-fuerte flex flex-col gap-2 border-t-[1.5px] pt-3.5">
          <div className="flex flex-wrap items-center gap-2">
            {validada ? null : (
              <>
                <Input
                  aria-label="Instrucción para regenerar"
                  value={instruccion}
                  onChange={(e) => setInstruccion(e.target.value)}
                  placeholder="Instrucción para regenerar: más breve, menciona la línea de alta tensión…"
                  className="h-11 min-w-60 flex-1 text-[0.8125rem]"
                />
                <Button
                  type="button"
                  variant="outline"
                  className="border-input h-11 rounded-xl px-3.5 text-[0.8125rem] shadow-none"
                  disabled={ocupada || cerrada}
                  onClick={() => regenerarLaSeccion(instruccion)}
                >
                  <RefreshCwIcon aria-hidden />
                  Regenerar
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="border-input h-11 rounded-xl px-3.5 text-[0.8125rem] shadow-none"
                  disabled={ocupada}
                  onClick={() => {
                    setBorrador(texto);
                    setEditando(true);
                  }}
                >
                  <PencilIcon aria-hidden />
                  Editar a mano
                </Button>
                <Button
                  type="button"
                  variant="fuerte"
                  className="h-11 rounded-xl px-4 text-[0.8125rem]"
                  disabled={ocupada}
                  onClick={() => actuar("validar")}
                >
                  <CheckIcon aria-hidden />
                  Validar sección
                </Button>
              </>
            )}

            {validada ? (
              <button
                type="button"
                disabled={ocupada}
                onClick={() => actuar("reabrir")}
                className="text-ink-secundario hover:text-ink px-1 text-xs font-bold underline-offset-2 hover:underline disabled:opacity-50"
              >
                Reabrir
              </button>
            ) : null}
          </div>

          {siguiente ? (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => onIr(siguiente.item_id)}
                className="text-success-soft-foreground inline-flex items-center gap-1 text-xs font-bold hover:underline"
              >
                Siguiente por revisar
                <ArrowRightIcon aria-hidden className="size-3.5" />
              </button>
            </div>
          ) : null}

          {!validada && errores > 0 ? (
            <p className="text-ink-muted text-xs font-semibold text-pretty">
              Hay {errores === 1 ? "1 error" : `${errores} errores`} de verificación. Puedes validar igual, pero revísalos
              antes de emitir el informe.
            </p>
          ) : null}
        </div>
      ) : null}

      {!editando && r.revisiones > 0 ? (
        <HistorialDeVersiones informeId={informeId} itemId={seccion.item_id} total={r.revisiones} />
      ) : null}
    </div>
  );
}
